import {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import DataContext from '../../contexts/DataContext';
import ConfigContext from '../../contexts/ConfigContext';
import Button from '../../components/Button';
import Input from '../../components/Input';

import Api from '../api';
import Slide from '../components/Slide';
import { energyConsumption as energyConsumptionPageData } from '../data/pages';
import {
  type BatterySiteCalcs,
  type SolarSiteCalcs,
  type SolarBatterySiteCalcs,
  type BatterySuccessPageData,
  type SolarSuccessPageData,
  type SolarBatterySuccessPageData,
  type SiteCalcRequest,
  type SiteCalcResponse,
  type CurrentSystemSiteCalcs,
  type CurrentSystemSuccessPageData,
} from '../types';
import { convertHomeSizeToEnergyConsumption } from '../utils';
import { UpgradeSystem } from '../enums';
import { appendScript } from '../../helpers';

interface FormElements extends HTMLFormControlsCollection {
  homeSize: HTMLInputElement;
  energyConsumption: HTMLInputElement;
}

interface EnergyConsumptionFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

export const getBatterySuccessPageData = (
  data: BatterySiteCalcs
): BatterySuccessPageData => {
  return {
    batterySize: data.battery_size,
    cost: data.cost_battery_only,
    monthlyCost: data.monthlycost_battery_only,
    paybackPeriod: data.payback_battery_only,
    savings25Years: data.savings25Years_battery_only,
  };
};

export const getSolarSuccessPageData = (
  data: SolarSiteCalcs
): SolarSuccessPageData => {
  return {
    upgradeSystemSize: data.upgrade_PVsize,
    upgradeSystemCount: data.upgrade_PVcount,
    cost: data.cost_solar_only,
    monthlyCost: data.monthlycost_solar_only,
    paybackPeriod: data.payback_solar_only,
    savings25Years: data.savings25Years_solar_only,
  };
};

export const getSolarBatterySuccessPageData = (
  data: SolarBatterySiteCalcs
): SolarBatterySuccessPageData => {
  return {
    upgradeSystemSize: data.upgrade_PVsize,
    upgradeSystemCount: data.upgrade_PVcount,
    batterySize: data.battery_size,
    cost: data.cost_solar_battery,
    monthlyCost: data.monthlycost_solar_battery,
    paybackPeriod: data.payback_solar_battery,
    savings25Years: data.savings25Years_solar_battery,
  };
};

export const getCurrentSystemSuccessPageData = (
  data: CurrentSystemSiteCalcs
): CurrentSystemSuccessPageData => {
  return {
    pvCount: data.current_PVcount,
    pvSize: data.current_PVsize,
  };
};

export default function EnergyConsumption() {
  const {
    enlightenData,
    setEnlightenData,
    urlParams,
    siteCalcHasError,
    setSiteCalcHasError,
    setIsLoadingSiteCalculations,
    lsid,
    slfApiUrl,
  } = useContext(AppContext);
  const { testName } = useContext(ConfigContext);
  const { data, updateData } = useContext(DataContext);
  const { upgradeSystem } = data;

  const [homeSizeInput, setHomeSizeInput] = useState(data.homeSize || '');
  const [energyConsumptionInput, setEnergyConsumptionInput] = useState(
    data.energyConsumption || ''
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [homeSizeHasError, setHomeSizeHasError] = useState(false);

  const navigate = useNavigate();
  const { heading, inputData } = energyConsumptionPageData;
  const { selectedSite, token } = enlightenData;
  const isButtonDisabled =
    homeSizeHasError ||
    !(homeSizeInput && energyConsumptionInput) ||
    isEditMode;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const invalidChars = ['e', 'E', '+', '-', ' '];
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleHomeSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const homeSizeInput = event.target.value.trim();
    if (homeSizeInput.length <= Number(inputData.homeSize.maxLength)) {
      const homeSizeNum = Number(homeSizeInput);
      if (homeSizeNum < 500 || homeSizeNum > 6000) {
        setHomeSizeHasError(true);
        setHomeSizeInput(homeSizeInput);
      } else {
        setHomeSizeHasError(false);
        setHomeSizeInput(homeSizeInput);
      }
      updateData({
        ...data,
        homeSize: homeSizeNum,
      });
    }
  };

  const handleEnergyConsumptionChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const energyConsumptionInput = event.target.value.trim();
    if (
      energyConsumptionInput.length <=
        Number(inputData.energyConsumption.maxLength) &&
      energyConsumptionInput !== '0'
    ) {
      setEnergyConsumptionInput(energyConsumptionInput);
      updateData({
        ...data,
        energyConsumption: Number(energyConsumptionInput),
      });
    }
  };

  const handleSubmit = (event: FormEvent<EnergyConsumptionFormElement>) => {
    event.preventDefault();

    if (process.env.REACT_APP_ENV !== 'local') {
      const lastSubmittedTime = localStorage.getItem('slf-last-submitted');
      if (lastSubmittedTime) {
        const now = Date.now();
        const secondsPassed = (now - Number(lastSubmittedTime)) / 1000;
        if (secondsPassed < 60 * 5) return;
      }
      localStorage.setItem('slf-last-submitted', String(Date.now()));
    }

    const homeSize = Number(homeSizeInput);
    const energyConsumption = Number(energyConsumptionInput);

    const siteCalcRequest: SiteCalcRequest = {
      monthlyUsage: energyConsumption * 30,
      siteId: selectedSite.siteId,
      token,
      zipCode: selectedSite.zipCode,
    };

    const createLeadRequest = {
      lsid,
      human_check: '',
      lead_type:
        upgradeSystem === UpgradeSystem.Solar
          ? 'Solar only'
          : upgradeSystem === UpgradeSystem.Battery
          ? 'Battery-only'
          : 'Solar + Battery',
      zip: selectedSite.siteMetadata.zip,
      state: selectedSite.siteMetadata.state,
      city: selectedSite.siteMetadata.city,
      street: selectedSite.siteMetadata.address1,
      country: selectedSite.siteMetadata.country,
      phone_number: '',
      email: selectedSite.siteMetadata.email,
      first_name: selectedSite.siteMetadata.first_name,
      last_name: selectedSite.siteMetadata.last_name,
      electric_utility: '',
      residence_ownership: '',
      monthly_elec: '',
      universal_leadid: (document.getElementById('leadid_token') as any)?.value,
      xxTrustedFormToken: (
        document.getElementById('xxTrustedFormToken_0') as any
      )?.value,
      xxTrustedFormCertUrl: (
        document.getElementById('xxTrustedFormCertUrl_0') as any
      )?.value,
      xxTrustedFormPingUrl: (
        document.getElementById('xxTrustedFormPingUrl_0') as any
      )?.value,
      landing_page: process.env.REACT_APP_ROOT_PATH!.replace('/', ''),
      'lp-type': 'ussp-multi-form',
      f: testName !== 'default' ? 'ab_test_' + testName : null,
      'meta:enphase_branded': 1,
    };

    setIsLoadingSiteCalculations(true);
    Api.createLead(createLeadRequest, urlParams, testName).then(
      (response) => {
        updateData({
          ...data,
          leadId: response.lead_id,
        });

        // Save customer data to on lead metadata
        const customerData = {
          homeSize,
          energyConsumption,
          selectedSite: enlightenData.selectedSite,
          customerHome: data.customerHome,
          upgradeSystem: data.upgradeSystem,
        };
        const customerDataUrl = `${slfApiUrl}/api/user/update_lead_meta/${
          response.lead_id
        }/customer_data/${encodeURIComponent(JSON.stringify(customerData))}`;
        appendScript(customerDataUrl);

        Api.getSiteCalculations(siteCalcRequest).then(
          (response: SiteCalcResponse) => {
            setIsLoadingSiteCalculations(false);
            const { site_calc_status: isSiteCalcAvailable } = response;

            if (isSiteCalcAvailable) {
              const successPageData = {
                battery: getBatterySuccessPageData(response.Battery_only),
                solar: getSolarSuccessPageData(response.Solar_Only),
                solarBattery: getSolarBatterySuccessPageData(
                  response.Solar_battery
                ),
                currentSystem: getCurrentSystemSuccessPageData(
                  response.Current_system
                ),
              };
              setEnlightenData({
                ...enlightenData,
                successPageData,
              });
            } else {
              setSiteCalcHasError(true);
              setIsLoadingSiteCalculations(false);
            }
          },
          (error) => {
            setSiteCalcHasError(true);
            setIsLoadingSiteCalculations(false);
            console.error('Error loading site calculations:', error);
          }
        );
      },
      (error) => {
        setSiteCalcHasError(true);
        setIsLoadingSiteCalculations(false);
        console.error('Error creating lead:', error);
      }
    );
    navigate(`/processing${urlParams}`);
  };

  const openEditMode = () => {
    setIsEditMode(true);
  };

  const closeEditMode = () => {
    setIsEditMode(false);
  };

  useEffect(() => {
    const energyConsumption = homeSizeInput
      ? convertHomeSizeToEnergyConsumption(Number(homeSizeInput))
      : '';
    setEnergyConsumptionInput(String(energyConsumption));
  }, [homeSizeInput]);

  return (
    <>
      <Slide heading={heading} />
      <form onSubmit={handleSubmit}>
        <p className="mt-[20px] md:mt-[30px] text-[16px] md:text-[18px]">
          {inputData.homeSize.label}
        </p>
        <div className="flex items-center justify-between w-full max-w-[294px] md:max-w-[400px] md-max:max-w-[279px] short:!w-[420px] short:!max-w-[420px] mt-3 md:mt-4 mb-6 md:mb-6">
          <Input
            errorMessage={inputData.homeSize.errorMessage}
            haveError={homeSizeHasError}
            maxLength={inputData.homeSize.maxLength}
            name={inputData.homeSize.name}
            onChange={handleHomeSizeChange}
            onKeyDown={handleKeyDown}
            placeholder={inputData.homeSize.placeholder}
            type={inputData.homeSize.type}
            unitLabel={inputData.homeSize.unit}
            value={homeSizeInput}
          />
        </div>
        <p className="mt-[20px] md:mt-[40px] text-[16px] md:text-[18px]">
          {inputData.energyConsumption.label}
        </p>
        <div className="flex items-center justify-between w-full max-w-[294px] md:max-w-[400px] md-max:max-w-[279px] short:!w-[420px] short:!max-w-[420px] mt-3 md:mt-4 mb-8 md:mb-16">
          <Input
            className="w-[155px] md:w-[260px] mr-[10px]"
            disabled={!isEditMode}
            maxLength={inputData.energyConsumption.maxLength}
            name={inputData.energyConsumption.name}
            onChange={handleEnergyConsumptionChange}
            onKeyDown={handleKeyDown}
            placeholder={inputData.energyConsumption.placeholder}
            type={inputData.homeSize.type}
            unitLabel={inputData.energyConsumption.unit}
            value={energyConsumptionInput ?? ''}
          />
          {isEditMode ? (
            <Button
              action={closeEditMode}
              className="!w-[125px] !md:w-[125px] bg-white !text-black
            font-medium border border-solid border-[red] md:hover:!bg-black md:hover:!text-white"
              title="Save"
              data-zuko-id="energy-consumption_edit_save"
            />
          ) : (
            <Button
              action={openEditMode}
              className="!w-[125px] !md:w-[125px] bg-white !text-black
            font-medium border border-solid border-black md:hover:!bg-black md:hover:!text-white"
              title="Edit"
              data-zuko-id="energy-consumption_edit"
            />
          )}
        </div>
        <div className="mt-[18px] md:mt-[40px]">
          <Button
            disabled={isButtonDisabled}
            type="submit"
            data-zuko-id="energy-consumption_continue"
          />
          {siteCalcHasError && (
            <p className="text-sm text-red-600 mt-2">
              Oops, something went wrong. Please try selecting another site.
            </p>
          )}
        </div>
      </form>
    </>
  );
}
